import { parseUsdInput, sanitizeUsdInput, usdToInput } from '@/shared/billing/currency';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAdicionales } from '@/presentation/hooks/useAdicionales';
import { useToast } from '@/presentation/contexts/ToastContext';
import { container } from '@/di';
import { TYPES } from '@/di/tokens';
import { CreateAdicionalAction, DeleteAdicionalAction, UpdateAdicionalAction } from '@/application/actions';
import { Button, ConfirmModal, ModalShell } from '@/presentation/components/common';
import { useAuth } from '@/presentation/contexts/AuthContext';
import type { Adicional } from '@/domain/entities/Adicional';

type TierDraft = {
  id: number;
  cupoInput: string;
  precioInput: string;
  /** Precio para clínicas en dólares; vacío = sin precio USD. */
  precioUsdInput: string;
  activo: boolean;
  savedCupo: number;
  savedPrecio: number;
  savedPrecioUsd: number | null;
  savedActivo: boolean;
};

const fieldClass =
  'block w-full rounded-md border border-slate-300 bg-white px-2.5 py-1 text-sm tabular-nums text-slate-900 shadow-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white';

const selectEstadoClass =
  'block w-[7.5rem] shrink-0 rounded-md border border-slate-300 bg-white px-2.5 py-1 text-sm text-slate-900 shadow-sm focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white';

const cardFillClass =
  'panel-card flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden';
const headerBarClass =
  'flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 py-1.5 dark:border-gray-700';
const tableScrollClass = 'min-h-0 min-w-0 flex-1 overflow-auto';
const tableBodyClass = 'min-w-full text-left text-sm';
const theadStickyClass =
  'panel-thead sticky top-0 z-10 text-[11px] uppercase tracking-wide text-slate-500';

function sanitizeDigitsInput(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (digits === '') return '';
  return digits.replace(/^0+(?=\d)/, '');
}

function parseDigitsOrZero(raw: string): number {
  const cleaned = sanitizeDigitsInput(raw);
  if (cleaned === '') return 0;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
}

function toTierDraft(a: Adicional): TierDraft | null {
  const cupo = a.cupoMensajes ?? 0;
  if (cupo <= 0) return null;
  return {
    id: a.id,
    cupoInput: String(cupo),
    precioInput: String(Math.round(a.precioUnitario)),
    precioUsdInput: usdToInput(a.precioUnitarioUsd),
    activo: a.isActive(),
    savedCupo: cupo,
    savedPrecio: a.precioUnitario,
    savedPrecioUsd: a.precioUnitarioUsd,
    savedActivo: a.isActive(),
  };
}

/**
 * Coofit Marketing — escalones de cupo email (recurrentes).
 * Trial / cortesía: bonificación por clínica (ficha comercial).
 */
export const CoofitMarketingPage: React.FC = () => {
  const { t } = useTranslation();
  const { success, error: showError } = useToast();
  const { canAdminister } = useAuth();

  const { adicionales, isLoading, error, refetch } = useAdicionales(undefined, undefined);

  const marketingItems = useMemo(
    () =>
      adicionales
        .filter((a) => a.isCoofitMarketing())
        .slice()
        .sort((a, b) => (a.cupoMensajes ?? 0) - (b.cupoMensajes ?? 0)),
    [adicionales],
  );

  const [tiers, setTiers] = useState<TierDraft[]>([]);
  const [savingTiers, setSavingTiers] = useState(false);
  const [creatingTier, setCreatingTier] = useState(false);
  const [deletingTier, setDeletingTier] = useState(false);
  const [isCreateTierOpen, setIsCreateTierOpen] = useState(false);
  const [newTierCupoInput, setNewTierCupoInput] = useState('');
  const [newTierPrecioInput, setNewTierPrecioInput] = useState('');
  const [tierToDelete, setTierToDelete] = useState<TierDraft | null>(null);

  useEffect(() => {
    setTiers(
      marketingItems
        .map(toTierDraft)
        .filter((x): x is TierDraft => x != null),
    );
  }, [marketingItems]);

  const tiersDirty = tiers.some(
    (tier) =>
      parseDigitsOrZero(tier.cupoInput) !== tier.savedCupo ||
      parseDigitsOrZero(tier.precioInput) !== tier.savedPrecio ||
      parseUsdInput(tier.precioUsdInput) !== tier.savedPrecioUsd ||
      tier.activo !== tier.savedActivo,
  );

  const readOnly = !canAdminister;
  const axiosErrorMessage = (err: unknown): string => {
    if (err && typeof err === 'object' && 'response' in err) {
      return String(
        (err as { response?: { data?: { message?: string } } }).response?.data?.message ?? '',
      );
    }
    return '';
  };

  const handleSaveTiers = async () => {
    if (!canAdminister || !tiersDirty) return;
    const parsed = tiers.map((tier) => ({
      tier,
      cupo: parseDigitsOrZero(tier.cupoInput),
      precio: parseDigitsOrZero(tier.precioInput),
      precioUsd: parseUsdInput(tier.precioUsdInput),
    }));
    for (const row of parsed) {
      if (row.precio < 0) {
        showError(`Precio inválido en escalón ${row.tier.id}`);
        return;
      }
      if (row.cupo < 1) {
        showError('Cada escalón activo necesita cupo de emails >= 1');
        return;
      }
    }
    const cupos = parsed.filter((r) => r.tier.activo).map((r) => r.cupo);
    if (new Set(cupos).size !== cupos.length) {
      showError('No puede haber dos escalones activos con el mismo cupo de emails');
      return;
    }
    setSavingTiers(true);
    try {
      const update = container.get<UpdateAdicionalAction>(TYPES.UpdateAdicionalAction);
      for (const { tier, cupo, precio, precioUsd } of parsed) {
        if (
          cupo === tier.savedCupo &&
          precio === tier.savedPrecio &&
          precioUsd === tier.savedPrecioUsd &&
          tier.activo === tier.savedActivo
        ) {
          continue;
        }
        await update.execute(tier.id, {
          nombre: `Coofit Marketing ${cupo}`,
          descripcion: `Email marketing. Incluye ${cupo} emails/mes.`,
          precioUnitario: precio,
          precioUnitarioUsd: precioUsd,
          unidad: 'mes',
          tipo: 'COOFIT_MARKETING',
          cupoMensajes: cupo,
          esRecurrente: true,
          activo: tier.activo,
        });
      }
      success('Escalones Marketing guardados');
      refetch();
    } catch (err) {
      showError(axiosErrorMessage(err) || (err instanceof Error ? err.message : t('common.error')));
    } finally {
      setSavingTiers(false);
    }
  };

  const handleCreateTier = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canAdminister) return;
    if (tiersDirty) {
      showError('Guardá los cambios de escalones antes de crear uno nuevo');
      return;
    }

    const cupo = parseDigitsOrZero(newTierCupoInput);
    const precio = parseDigitsOrZero(newTierPrecioInput);
    if (cupo < 1) {
      showError('Indicá un cupo de emails >= 1');
      return;
    }
    if (precio < 0) {
      showError('El precio debe ser mayor o igual a 0');
      return;
    }
    if (tiers.some((tier) => parseDigitsOrZero(tier.cupoInput) === cupo)) {
      showError('Ya existe un escalón con ese cupo');
      return;
    }

    setCreatingTier(true);
    try {
      const create = container.get<CreateAdicionalAction>(TYPES.CreateAdicionalAction);
      await create.execute({
        nombre: `Coofit Marketing ${cupo}`,
        descripcion: `Email marketing. Incluye ${cupo} emails/mes.`,
        precioUnitario: precio,
        unidad: 'mes',
        tipo: 'COOFIT_MARKETING',
        cupoMensajes: cupo,
        esRecurrente: true,
        activo: true,
      });
      success('Escalón Marketing creado');
      setIsCreateTierOpen(false);
      setNewTierCupoInput('');
      setNewTierPrecioInput('');
      refetch();
    } catch (err) {
      showError(axiosErrorMessage(err) || (err instanceof Error ? err.message : t('common.error')));
    } finally {
      setCreatingTier(false);
    }
  };

  const handleDeleteTier = async () => {
    if (!tierToDelete || !canAdminister) return;
    if (tiersDirty) {
      showError('Guardá los cambios de escalones antes de eliminar');
      return;
    }
    setDeletingTier(true);
    try {
      const remove = container.get<DeleteAdicionalAction>(TYPES.DeleteAdicionalAction);
      await remove.execute(tierToDelete.id);
      success('Escalón Marketing eliminado');
      setTierToDelete(null);
      refetch();
    } catch (err) {
      const status =
        err && typeof err === 'object' && 'response' in err
          ? Number((err as { response?: { status?: number } }).response?.status ?? 0)
          : 0;
      showError(
        status === 409
          ? 'No se puede eliminar: está asignado o tiene historial. Pausalo en el catálogo.'
          : axiosErrorMessage(err) || (err instanceof Error ? err.message : t('common.error')),
      );
    } finally {
      setDeletingTier(false);
    }
  };

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col gap-3 overflow-hidden">
      {(error || isLoading) && (
        <div
          className={`shrink-0 ${
            error
              ? 'rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200'
              : 'rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600 dark:border-gray-700 dark:bg-gray-800 dark:text-slate-300'
          }`}
        >
          {error?.message || 'Cargando catálogo…'}
        </div>
      )}

      <section className={cardFillClass}>
        <div className={headerBarClass}>
          <h2 className="text-sm font-semibold text-slate-900 dark:text-white">
            Escalones Marketing
          </h2>
          {canAdminister ? (
            <div className="flex flex-wrap gap-1.5">
              <Button
                variant="secondary"
                size="sm"
                disabled={savingTiers || creatingTier || deletingTier}
                onClick={() => {
                  if (tiersDirty) {
                    showError('Guardá los cambios de escalones antes de crear uno nuevo');
                    return;
                  }
                  setIsCreateTierOpen(true);
                }}
              >
                Nuevo escalón
              </Button>
              <Button
                variant="primary"
                size="sm"
                isLoading={savingTiers}
                disabled={!tiersDirty || creatingTier || deletingTier}
                onClick={() => void handleSaveTiers()}
              >
                Guardar escalones
              </Button>
            </div>
          ) : null}
        </div>
        {tiers.length === 0 ? (
          <p className="px-4 py-4 text-sm text-slate-500">
            No hay escalones. Corré <code className="text-xs">npm run migrate</code> en el API del
            Panel.
          </p>
        ) : (
          <div className={tableScrollClass}>
            <table className={tableBodyClass}>
              <thead className={theadStickyClass}>
                <tr>
                  <th className="px-4 py-1.5 font-medium">Escalón</th>
                  <th className="px-4 py-1.5 font-medium">Emails / mes</th>
                  <th className="px-4 py-1.5 font-medium">Precio mensual</th>
                  <th className="px-4 py-1.5 font-medium">Precio USD</th>
                  <th className="px-4 py-1.5 font-medium">Estado</th>
                  {canAdminister ? <th className="px-4 py-1.5 font-medium">Acciones</th> : null}
                </tr>
              </thead>
              <tbody>
                {tiers.map((tier) => {
                  const cupoNum = parseDigitsOrZero(tier.cupoInput);
                  return (
                    <tr
                      key={tier.id}
                      className="border-t border-slate-100 dark:border-gray-700"
                    >
                      <td className="px-4 py-1.5 font-medium text-slate-900 dark:text-white">
                        Coofit Marketing{' '}
                        {cupoNum > 0 ? cupoNum.toLocaleString('es-AR') : '…'}
                      </td>
                      <td className="px-4 py-1.5">
                        <input
                          type="text"
                          inputMode="numeric"
                          autoComplete="off"
                          value={tier.cupoInput}
                          disabled={readOnly}
                          onChange={(e) => {
                            const cupoInput = sanitizeDigitsInput(e.target.value);
                            setTiers((prev) =>
                              prev.map((x) => (x.id === tier.id ? { ...x, cupoInput } : x)),
                            );
                          }}
                          className={`${fieldClass} max-w-[7.5rem]`}
                        />
                      </td>
                      <td className="px-4 py-1.5">
                        <div className="relative max-w-[9rem]">
                          <span className="pointer-events-none absolute inset-y-0 left-2.5 flex items-center text-sm text-slate-400">
                            $
                          </span>
                          <input
                            type="text"
                            inputMode="numeric"
                            autoComplete="off"
                            value={tier.precioInput}
                            disabled={readOnly}
                            onChange={(e) => {
                              const precioInput = sanitizeDigitsInput(e.target.value);
                              setTiers((prev) =>
                                prev.map((x) => (x.id === tier.id ? { ...x, precioInput } : x)),
                              );
                            }}
                            className={`${fieldClass} pl-6`}
                          />
                        </div>
                      </td>
                      <td className="px-4 py-1.5">
                        <div className="relative max-w-[9rem]">
                          <span className="pointer-events-none absolute inset-y-0 left-2.5 flex items-center text-sm text-slate-400">
                            US$
                          </span>
                          <input
                            type="text"
                            inputMode="decimal"
                            autoComplete="off"
                            placeholder="—"
                            value={tier.precioUsdInput}
                            disabled={readOnly}
                            onChange={(e) => {
                              const precioUsdInput = sanitizeUsdInput(e.target.value);
                              setTiers((prev) =>
                                prev.map((x) => (x.id === tier.id ? { ...x, precioUsdInput } : x)),
                              );
                            }}
                            className={`${fieldClass} pl-10`}
                          />
                        </div>
                      </td>
                      <td className="px-4 py-1.5">
                        <select
                          value={tier.activo ? 'activo' : 'pausado'}
                          disabled={readOnly}
                          onChange={(e) => {
                            const activo = e.target.value === 'activo';
                            setTiers((prev) =>
                              prev.map((x) => (x.id === tier.id ? { ...x, activo } : x)),
                            );
                          }}
                          className={selectEstadoClass}
                        >
                          <option value="activo">Activo</option>
                          <option value="pausado">Pausado</option>
                        </select>
                      </td>
                      {canAdminister ? (
                        <td className="px-4 py-1.5">
                          <Button
                            variant="danger"
                            size="sm"
                            disabled={savingTiers || creatingTier || deletingTier}
                            onClick={() => {
                              if (tiersDirty) {
                                showError('Guardá los cambios de escalones antes de eliminar');
                                return;
                              }
                              setTierToDelete(tier);
                            }}
                          >
                            Eliminar
                          </Button>
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <ModalShell
        isOpen={isCreateTierOpen}
        onClose={() => {
          if (creatingTier) return;
          setIsCreateTierOpen(false);
        }}
        title="Nuevo escalón Marketing"
        size="md"
      >
        <form className="space-y-3" onSubmit={(e) => void handleCreateTier(e)}>
          <p className="text-xs text-slate-500">
            Definí el cupo de emails incluidos y el precio mensual del nuevo escalón.
          </p>
          <label className="block text-xs font-medium text-slate-700 dark:text-slate-200">
            Emails incluidos / mes
            <input
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={newTierCupoInput}
              onChange={(e) => setNewTierCupoInput(sanitizeDigitsInput(e.target.value))}
              className={`${fieldClass} mt-1`}
            />
          </label>
          <label className="block text-xs font-medium text-slate-700 dark:text-slate-200">
            Precio mensual
            <div className="relative mt-1">
              <span className="pointer-events-none absolute inset-y-0 left-2.5 flex items-center text-sm text-slate-400">
                $
              </span>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="off"
                value={newTierPrecioInput}
                onChange={(e) => setNewTierPrecioInput(sanitizeDigitsInput(e.target.value))}
                className={`${fieldClass} pl-6`}
              />
            </div>
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={creatingTier}
              onClick={() => setIsCreateTierOpen(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" variant="primary" size="sm" isLoading={creatingTier}>
              Crear escalón
            </Button>
          </div>
        </form>
      </ModalShell>

      <ConfirmModal
        isOpen={tierToDelete != null}
        onClose={() => {
          if (deletingTier) return;
          setTierToDelete(null);
        }}
        onConfirm={() => void handleDeleteTier()}
        title="Eliminar escalón Marketing"
        message={
          tierToDelete
            ? `¿Querés eliminar Coofit Marketing ${tierToDelete.savedCupo} del catálogo?`
            : ''
        }
        confirmText="Eliminar"
        isLoading={deletingTier}
        variant="danger"
      />
    </div>
  );
};
